import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Calendar, Clock, MapPin, DollarSign, 
  User, Mail, Phone, Home, CheckCircle, XCircle, 
  AlertCircle, Edit, Save, Trash2, Star
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import '../dashboard/Dashboard.module.css';
import { api } from '../../utils/api';
import AdminLayout from '../common/AdminLayout';

interface Inquiry {
  id: number;
  inquiry_reference: string;
  title: string;
  description: string;
  status: string;
  priority: string;
  is_read: boolean;
  scheduled_date: string;
  scheduled_time: string;
  location_address: string;
  latitude: number | null;
  longitude: number | null;
  budget_min: number | null;
  budget_max: number | null;
  client_notes: string | null;
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
  category_name: string;
  skill_name: string | null;
  client_id: number;
  client_first_name: string;
  client_last_name: string;
  client_email: string;
  client_phone: string;
  client_address: string;
  client_parish: string;
}

const InquiryDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [inquiry, setInquiry] = useState<Inquiry | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingStatus, setEditingStatus] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const [statusUpdateError, setStatusUpdateError] = useState('');
  const [editingNotes, setEditingNotes] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [notesUpdateLoading, setNotesUpdateLoading] = useState(false);
  const [notesUpdateError, setNotesUpdateError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const { admin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchInquiry();
  }, [id]);

  const fetchInquiry = async () => {
    setLoading(true);
    setError('');
    
    try {
      const data = await api.get(`/api/admin/inquiries/${id}`);
      
      if (data.success) {
        setInquiry(data.inquiry);
        setNewStatus(data.inquiry.status);
        setAdminNotes(data.inquiry.admin_notes || '');
      } else {
        setError('Failed to load inquiry details');
      }
    } catch (err) {
      console.error('Error fetching inquiry:', err);
      setError('Failed to load inquiry details. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!inquiry) return;
    
    setStatusUpdateLoading(true);
    setStatusUpdateError('');
    
    try {
      const data = await api.patch(`/api/admin/inquiries/${id}/status`, { status: newStatus });
      
      if (data.success) {
        setInquiry({
          ...inquiry,
          status: newStatus,
          updated_at: new Date().toISOString()
        });
        setEditingStatus(false);
      } else {
        setStatusUpdateError('Failed to update status');
      }
    } catch (err) {
      console.error('Error updating status:', err);
      setStatusUpdateError('Failed to update status. Please try again later.');
    } finally {
      setStatusUpdateLoading(false);
    }
  };

  const handleUpdateNotes = async () => {
    if (!inquiry) return;
    
    setNotesUpdateLoading(true);
    setNotesUpdateError('');
    
    try {
      const data = await api.patch(`/api/admin/inquiries/${id}/notes`, { admin_notes: adminNotes });
      
      if (data.success) {
        setInquiry({
          ...inquiry,
          admin_notes: adminNotes,
          updated_at: new Date().toISOString()
        });
        setEditingNotes(false);
      } else {
        setNotesUpdateError('Failed to update notes');
      }
    } catch (err) {
      console.error('Error updating notes:', err);
      setNotesUpdateError('Failed to update notes. Please try again later.');
    } finally {
      setNotesUpdateLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    setDeleteError('');
    
    try {
      const data = await api.delete(`/api/admin/inquiries/${id}`);
      
      if (data.success) {
        navigate('/admin/inquiries');
      } else {
        setDeleteError('Failed to delete inquiry');
      }
    } catch (err) {
      console.error('Error deleting inquiry:', err);
      setDeleteError('Failed to delete inquiry. Please try again later.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const renderPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'low':
        return <span className="badge bg-info">Low</span>;
      case 'medium':
        return <span className="badge bg-primary">Medium</span>;
      case 'high':
        return <span className="badge bg-warning">High</span>;
      case 'urgent':
        return <span className="badge bg-danger">Urgent</span>;
      default:
        return <span className="badge bg-secondary">{priority}</span>;
    }
  };

  const renderStatusBadge = (status: string) => {
    switch (status) {
      case 'new':
        return <span className="badge bg-info d-flex align-items-center gap-1"><AlertCircle size={14} /> New</span>;
      case 'in_progress':
        return <span className="badge bg-warning d-flex align-items-center gap-1"><Clock size={14} /> In Progress</span>;
      case 'completed':
        return <span className="badge bg-success d-flex align-items-center gap-1"><CheckCircle size={14} /> Completed</span>;
      case 'cancelled':
        return <span className="badge bg-danger d-flex align-items-center gap-1"><XCircle size={14} /> Cancelled</span>;
      default:
        return <span className="badge bg-secondary">{status}</span>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatTime = (timeString: string) => {
    // Format time from "14:30:00" to "2:30 PM"
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const formattedHour = hour % 12 || 12;
    return `${formattedHour}:${minutes} ${ampm}`;
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error || !inquiry) {
    return (
      <div className="alert alert-danger">
        {error || 'Inquiry not found'}
      </div>
    );
  }

  return (
    <AdminLayout title="Inquiry Details" subtitle="View and manage inquiry information">
      <div className="inquiry-detail">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center">
            <button 
              className="btn btn-outline-secondary me-3"
              onClick={() => navigate('/admin/inquiries')}
            >
              <ArrowLeft size={18} />
            </button>
          </div>
          <div className="d-flex gap-2">
            <button 
              className="btn btn-outline-danger"
              onClick={() => setShowDeleteModal(true)}
            >
              <Trash2 size={18} className="me-1" /> Delete Inquiry
            </button>
          </div>
        </div>

      <div className="row mb-4">
        <div className="col-md-8">
          <div className="card mb-4">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Inquiry Information</h5>
              <div className="d-flex align-items-center">
                <div className="me-3">
                  <span className="text-muted me-2">Reference:</span>
                  <strong>{inquiry.inquiry_reference}</strong>
                </div>
                {renderStatusBadge(inquiry.status)}
              </div>
            </div>
            <div className="card-body">
              <h4>{inquiry.title}</h4>
              
              <div className="mb-4">
                <span className="badge bg-secondary me-2">{inquiry.category_name}</span>
                {inquiry.skill_name && <span className="badge bg-secondary">{inquiry.skill_name}</span>}
              </div>
              
              <h6>Description</h6>
              <p className="mb-4">{inquiry.description}</p>
              
              <div className="row mb-4">
                <div className="col-md-6">
                  <div className="d-flex align-items-center mb-3">
                    <Calendar size={18} className="text-primary me-2" />
                    <div>
                      <div className="text-muted small">Scheduled Date</div>
                      <strong>{formatDate(inquiry.scheduled_date)}</strong>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-flex align-items-center mb-3">
                    <Clock size={18} className="text-primary me-2" />
                    <div>
                      <div className="text-muted small">Scheduled Time</div>
                      <strong>{formatTime(inquiry.scheduled_time)}</strong>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="d-flex align-items-center mb-3">
                <MapPin size={18} className="text-primary me-2" />
                <div>
                  <div className="text-muted small">Location</div>
                  <strong>{inquiry.location_address}</strong>
                </div>
              </div>
              
              {(inquiry.budget_min || inquiry.budget_max) && (
                <div className="d-flex align-items-center mb-3">
                  <DollarSign size={18} className="text-primary me-2" />
                  <div>
                    <div className="text-muted small">Budget</div>
                    <strong>
                      {inquiry.budget_min && inquiry.budget_max 
                        ? `JMD $${inquiry.budget_min} - $${inquiry.budget_max}`
                        : inquiry.budget_min 
                          ? `JMD $${inquiry.budget_min}+`
                          : `Up to JMD $${inquiry.budget_max}`
                      }
                    </strong>
                  </div>
                </div>
              )}
              
              <div className="d-flex align-items-center mb-3">
                <Star size={18} className="text-primary me-2" />
                <div>
                  <div className="text-muted small">Priority</div>
                  <div>{renderPriorityBadge(inquiry.priority)}</div>
                </div>
              </div>
              
              {inquiry.client_notes && (
                <div className="mt-4">
                  <h6>Client Notes</h6>
                  <p className="mb-0">{inquiry.client_notes}</p>
                </div>
              )}
            </div>
            <div className="card-footer text-muted">
              <small>Created: {formatDateTime(inquiry.created_at)}</small>
              <br />
              <small>Last Updated: {formatDateTime(inquiry.updated_at)}</small>
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Admin Notes</h5>
              {!editingNotes ? (
                <button 
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => setEditingNotes(true)}
                >
                  <Edit size={16} className="me-1" /> Edit Notes
                </button>
              ) : (
                <div className="d-flex gap-2">
                  <button 
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => {
                      setEditingNotes(false);
                      setAdminNotes(inquiry.admin_notes || '');
                    }}
                  >
                    Cancel
                  </button>
                  <button 
                    className="btn btn-primary btn-sm"
                    onClick={handleUpdateNotes}
                    disabled={notesUpdateLoading}
                  >
                    {notesUpdateLoading ? (
                      <>
                        <div className="spinner-border spinner-border-sm me-1" role="status"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save size={16} className="me-1" /> Save
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
            <div className="card-body">
              {notesUpdateError && (
                <div className="alert alert-danger mb-3">
                  {notesUpdateError}
                </div>
              )}
              
              {!editingNotes ? (
                inquiry.admin_notes ? (
                  <p className="mb-0">{inquiry.admin_notes}</p>
                ) : (
                  <p className="text-muted mb-0">No admin notes yet.</p>
                )
              ) : (
                <div className="form-group">
                  <textarea
                    className="form-control"
                    rows={5}
                    value={adminNotes}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setAdminNotes(e.target.value)}
                    placeholder="Add notes about this inquiry..."
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="col-md-4">
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">Status Management</h5>
            </div>
            <div className="card-body">
              {statusUpdateError && (
                <div className="alert alert-danger mb-3">
                  {statusUpdateError}
                </div>
              )}
              
              <div className="mb-3">
                <div className="text-muted mb-2">Current Status</div>
                {!editingStatus ? (
                  <div className="d-flex justify-content-between align-items-center">
                    <div>{renderStatusBadge(inquiry.status)}</div>
                    <button 
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => setEditingStatus(true)}
                    >
                      <Edit size={16} className="me-1" /> Change
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="form-group mb-3">
                      <select
                        className="form-select"
                        value={newStatus}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setNewStatus(e.target.value)}
                      >
                        <option value="new">New</option>
                        <option value="in_progress">In Progress</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                    
                    <div className="d-flex gap-2">
                      <button 
                        className="btn btn-outline-secondary"
                        onClick={() => {
                          setEditingStatus(false);
                          setNewStatus(inquiry.status);
                        }}
                      >
                        Cancel
                      </button>
                      <button 
                        className="btn btn-primary"
                        onClick={handleUpdateStatus}
                        disabled={statusUpdateLoading}
                      >
                        {statusUpdateLoading ? (
                          <>
                            <div className="spinner-border spinner-border-sm me-1" role="status"></div>
                            Updating...
                          </>
                        ) : (
                          'Update Status'
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Client Information</h5>
            </div>
            <div className="card-body">
              <div className="d-flex align-items-center mb-3">
                <User size={18} className="text-primary me-2" />
                <div>
                  <div className="text-muted small">Name</div>
                  <strong>{inquiry.client_first_name} {inquiry.client_last_name}</strong>
                </div>
              </div>
              
              <div className="d-flex align-items-center mb-3">
                <Mail size={18} className="text-primary me-2" />
                <div>
                  <div className="text-muted small">Email</div>
                  <a href={`mailto:${inquiry.client_email}`}>{inquiry.client_email}</a>
                </div>
              </div>
              
              {inquiry.client_phone && (
                <div className="d-flex align-items-center mb-3">
                  <Phone size={18} className="text-primary me-2" />
                  <div>
                    <div className="text-muted small">Phone</div>
                    <a href={`tel:${inquiry.client_phone}`}>{inquiry.client_phone}</a>
                  </div>
                </div>
              )}
              
              {inquiry.client_address && (
                <div className="d-flex align-items-center mb-3">
                  <Home size={18} className="text-primary me-2" />
                  <div>
                    <div className="text-muted small">Address</div>
                    <div>{inquiry.client_address}</div>
                    {inquiry.client_parish && (
                      <div className="text-muted">{inquiry.client_parish}</div>
                    )}
                  </div>
                </div>
              )}
              
              <div className="mt-3">
                <button 
                  className="btn btn-outline-primary" 
                  onClick={() => navigate(`/admin/clients/${inquiry.client_id}`)}
                >
                  View Client Profile
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal fade show" style={{ display: 'block' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Deletion</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)}></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this inquiry?</p>
                <p className="text-danger"><strong>This action cannot be undone.</strong></p>
                {deleteError && <div className="alert alert-danger">{deleteError}</div>}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </button>
                <button 
                  className="btn btn-danger" 
                  onClick={handleDelete}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? (
                    <>
                      <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                      Deleting...
                    </>
                  ) : (
                    'Delete'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </AdminLayout>
  );
};

export default InquiryDetail;